/**
 * Fun commands including games, animations, tools, and easter eggs.
 * These commands provide entertainment and showcase terminal capabilities.
 *
 * @module terminal/commands/fun
 */
import type { CommandResult, CommandContext } from './types';
import { ASCII, cowsay, JOKES, FORTUNES } from './ascii';

/**
 * Generates ASCII art text using block characters.
 * Supports A-Z, 0-9, and basic punctuation. Limited to 20 characters.
 *
 * @param text - The text to convert to ASCII art
 * @returns Multi-line ASCII art string
 *
 * @example
 * ```
 * generateAsciiArt('HI')
 * // Returns:
 * // █   █   █████
 * // █   █     █
 * // █████     █
 * // █   █     █
 * // █   █   █████
 * ```
 */
function generateAsciiArt(text: string): string {
	const letters: Record<string, string[]> = {
		A: ['  █  ', ' █ █ ', '█████', '█   █', '█   █'],
		B: ['████ ', '█   █', '████ ', '█   █', '████ '],
		C: [' ████', '█    ', '█    ', '█    ', ' ████'],
		D: ['████ ', '█   █', '█   █', '█   █', '████ '],
		E: ['█████', '█    ', '████ ', '█    ', '█████'],
		F: ['█████', '█    ', '████ ', '█    ', '█    '],
		G: [' ████', '█    ', '█  ██', '█   █', ' ████'],
		H: ['█   █', '█   █', '█████', '█   █', '█   █'],
		I: ['█████', '  █  ', '  █  ', '  █  ', '█████'],
		J: ['█████', '    █', '    █', '█   █', ' ███ '],
		K: ['█   █', '█  █ ', '███  ', '█  █ ', '█   █'],
		L: ['█    ', '█    ', '█    ', '█    ', '█████'],
		M: ['█   █', '██ ██', '█ █ █', '█   █', '█   █'],
		N: ['█   █', '██  █', '█ █ █', '█  ██', '█   █'],
		O: [' ███ ', '█   █', '█   █', '█   █', ' ███ '],
		P: ['████ ', '█   █', '████ ', '█    ', '█    '],
		Q: [' ███ ', '█   █', '█ █ █', '█  █ ', ' ██ █'],
		R: ['████ ', '█   █', '████ ', '█  █ ', '█   █'],
		S: [' ████', '█    ', ' ███ ', '    █', '████ '],
		T: ['█████', '  █  ', '  █  ', '  █  ', '  █  '],
		U: ['█   █', '█   █', '█   █', '█   █', ' ███ '],
		V: ['█   █', '█   █', '█   █', ' █ █ ', '  █  '],
		W: ['█   █', '█   █', '█ █ █', '██ ██', '█   █'],
		X: ['█   █', ' █ █ ', '  █  ', ' █ █ ', '█   █'],
		Y: ['█   █', ' █ █ ', '  █  ', '  █  ', '  █  '],
		Z: ['█████', '   █ ', '  █  ', ' █   ', '█████'],
		' ': ['     ', '     ', '     ', '     ', '     '],
		'!': ['  █  ', '  █  ', '  █  ', '     ', '  █  '],
		'?': [' ███ ', '█   █', '  ██ ', '     ', '  █  '],
		'.': ['     ', '     ', '     ', '     ', '  █  '],
		'0': [' ███ ', '█  ██', '█ █ █', '██  █', ' ███ '],
		'1': ['  █  ', ' ██  ', '  █  ', '  █  ', '█████'],
		'2': [' ███ ', '█   █', '  ██ ', ' █   ', '█████'],
		'3': ['████ ', '    █', ' ███ ', '    █', '████ '],
		'4': ['█   █', '█   █', '█████', '    █', '    █'],
		'5': ['█████', '█    ', '████ ', '    █', '████ '],
		'6': [' ███ ', '█    ', '████ ', '█   █', ' ███ '],
		'7': ['█████', '    █', '   █ ', '  █  ', '  █  '],
		'8': [' ███ ', '█   █', ' ███ ', '█   █', ' ███ '],
		'9': [' ███ ', '█   █', ' ████', '    █', ' ███ ']
	};
	const upper = text.toUpperCase().slice(0, 20);
	const rows: string[] = [];
	for (let r = 0; r < 5; r++) {
		let line = '';
		for (const ch of upper) {
			const l = letters[ch] || letters['?'];
			line += l[r] + ' ';
		}
		rows.push(line);
	}
	return rows.join('\n');
}

/**
 * Fun commands for entertainment and interactive features.
 *
 * Categories:
 * - **Jokes & Fortune**: Random jokes and fortune cookie messages
 * - **ASCII Art**: cowsay, custom ASCII text generation
 * - **Games**: snake, tetris, pong, breakout, tictactoe, typing, guess
 * - **Animations**: matrix, fireworks, hack, glitch, rain, particles, nyancat, coffee
 * - **Tools**: pomodoro timer, countdown timer, JSON formatter
 * - **Effects**: spin, flip, barrel roll, CRT filter
 * - **Easter Eggs**: sl, iddqd, idkfa, rickroll, 42
 *
 * @example
 * ```
 * joke          - Random programming joke
 * fortune       - Fortune cookie message
 * cowsay <msg>  - Cow says your message
 * ascii <text>  - Generate ASCII art text
 * snake         - Play Snake game
 * matrix        - Matrix rain animation
 * pomodoro      - Start Pomodoro timer
 * ```
 */
export const funCommands: Record<
	string,
	(args: string[], context: CommandContext) => CommandResult | null
> = {
	joke: () => ({
		type: 'text',
		lines: [JOKES[Math.floor(Math.random() * JOKES.length)]]
	}),

	fortune: () => ({
		type: 'text',
		lines: ['🔮 ' + FORTUNES[Math.floor(Math.random() * FORTUNES.length)]]
	}),

	cowsay: (args) => ({
		type: 'ascii',
		content: cowsay(args.join(' ') || 'Moo!')
	}),

	ascii: (args) => {
		const text = args.join(' ');
		if (!text) {
			return { type: 'text', lines: ['Usage: ascii <text> (max 20 characters)'] };
		}
		return { type: 'ascii', content: generateAsciiArt(text) };
	},

	weather: () => ({
		type: 'text',
		lines: [
			'┌────────────────────────────────────┐',
			'│  WEATHER - Kristiansand, Norway     │',
			'├────────────────────────────────────┤',
			'│                                     │',
			'│    \\   /     Partly Cloudy          │',
			'│     .-.      Temperature: 12°C      │',
			'│  ― (   ) ―  Wind: 8 km/h SW        │',
			"│     `-'      Humidity: 72%           │",
			'│    /   \\     UV Index: 3             │',
			'│                                     │',
			'│  Forecast: Nice enough to code      │',
			'│  outside. But why would you?         │',
			'└────────────────────────────────────┘'
		]
	}),

	hello: () => ({ type: 'text', lines: ['Hello, human!'] }),
	hi: () => ({ type: 'text', lines: ['Hi there!'] }),

	// Easter eggs
	'42': () => ({
		type: 'text',
		lines: ['The Answer to the Ultimate Question of Life, The Universe, and Everything.']
	}),

	sl: () => ({
		type: 'ascii',
		content: ASCII.train + '\n\nYou meant "ls", right?'
	}),

	iddqd: () => ({
		type: 'ascii',
		content: ASCII.doom
	}),

	idkfa: () => ({
		type: 'text',
		lines: ['All weapons and keys acquired. Now go frag some demons!']
	}),

	rickroll: () => ({
		type: 'ascii',
		content: ASCII.rickroll + '\n\nNever gonna give you up! Never gonna let you down!'
	}),

	nyan: () => ({
		type: 'animation',
		animation: 'nyancat'
	}),

	// Games
	snake: () => ({ type: 'game', game: 'snake' }),
	tetris: () => ({ type: 'game', game: 'tetris' }),
	pong: () => ({ type: 'game', game: 'pong' }),
	guess: () => ({ type: 'game', game: 'guess' }),
	breakout: () => ({ type: 'game', game: 'breakout' }),
	tictactoe: () => ({ type: 'game', game: 'tictactoe' }),
	typing: () => ({ type: 'game', game: 'typing' }),

	// Animations
	matrix: () => ({ type: 'animation', animation: 'matrix' }),
	fireworks: () => ({ type: 'animation', animation: 'fireworks' }),
	hack: () => ({ type: 'animation', animation: 'hack' }),
	glitch: () => ({ type: 'animation', animation: 'glitch' }),
	rain: () => ({ type: 'animation', animation: 'rain' }),
	particles: () => ({ type: 'animation', animation: 'particles' }),
	typewriter: () => ({ type: 'animation', animation: 'typewriter' }),
	nyancat: () => ({ type: 'animation', animation: 'nyancat' }),
	coffee: () => ({ type: 'animation', animation: 'coffee' }),

	// Tools
	pomodoro: () => ({ type: 'tool', tool: 'pomodoro' }),
	timer: (args) => ({
		type: 'tool',
		tool: 'timer',
		lines: args.length ? [args.join(' ')] : undefined
	}),
	json: () => ({ type: 'tool', tool: 'json' }),

	// Effects
	spin: () => ({ type: 'effect', effect: 'spin' }),
	flip: () => ({ type: 'effect', effect: 'flip' }),
	barrel: () => ({ type: 'effect', effect: 'barrel' }),
	crt: () => ({ type: 'effect', effect: 'crt' })
};
